import { Injectable, Inject, PLATFORM_ID, OnDestroy } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { HttpClient, HttpHeaders, HttpRequest } from '@angular/common/http';
import { BehaviorSubject, Observable, Subject, from, of } from 'rxjs';
import { catchError, takeUntil, tap, concatMap, delay, finalize } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

export interface QueuedRequest {
  id: string;
  method: string;
  url: string;
  body: any;
  headers: { [key: string]: string };
  timestamp: number;
  retryCount: number;
}

interface NetworkInformationLike {
  type?: string;
  effectiveType?: string;
  downlink?: number;
  rtt?: number;
  addEventListener?: (type: string, listener: EventListenerOrEventListenerObject) => void;
  removeEventListener?: (
    type: string,
    listener: EventListenerOrEventListenerObject
  ) => void;
}

type NavigatorWithConnection = Navigator & {
  connection?: NetworkInformationLike;
  mozConnection?: NetworkInformationLike;
  webkitConnection?: NetworkInformationLike;
};

@Injectable({
  providedIn: 'root',
})
export class NetworkStatusService implements OnDestroy {
  private readonly MAX_QUEUE_SIZE = 50;
  private readonly MAX_RETRY_ATTEMPTS = 3;
  private readonly STORAGE_KEY = 'bidxsell_request_queue';

  private isOnlineSubject = new BehaviorSubject<boolean>(true);
  private queuedRequestsSubject = new BehaviorSubject<QueuedRequest[]>([]);
  private isRetryingSubject = new BehaviorSubject<boolean>(false);
  private destroy$ = new Subject<void>();
  private readonly onlineEventHandler = () => this.handleOnline();
  private readonly offlineEventHandler = () => this.handleOffline();
  private readonly connectionChangeHandler = () => this.handleConnectionChange();
  private browserOnlineHint = true;
  private internetReachable = true;
  private reachabilityCheckInProgress = false;
  private lastReachabilityCheckAt = 0;
  private pusherProbeTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly reachabilityThrottleMs = 2000;
  private readonly pusherProbeDebounceMs = 1200;
  private readonly reachabilityTimeoutMs = 5000;

  isOnline$ = this.isOnlineSubject.asObservable();
  queuedRequests$ = this.queuedRequestsSubject.asObservable();
  isRetrying$ = this.isRetryingSubject.asObservable();

  // URLs that should NOT be queued (auth-related)
  private excludedUrls = ['/login', '/logout', '/refresh-token', '/register'];

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private http: HttpClient
  ) {
    this.initializeNetworkListeners();
    this.loadQueueFromStorage();
  }

  private initializeNetworkListeners(): void {
    if (isPlatformBrowser(this.platformId)) {
      // Set initial state
      this.browserOnlineHint = navigator.onLine;
      this.internetReachable = navigator.onLine;
      this.updateOnlineState('init');

      // Listen for online/offline events
      window.addEventListener('online', this.onlineEventHandler);
      window.addEventListener('offline', this.offlineEventHandler);

      // Listen for network interface changes (if supported by browser)
      const networkInfo = this.getNetworkInformation();
      if (networkInfo?.addEventListener) {
        networkInfo.addEventListener('change', this.connectionChangeHandler);
      }
    }
  }

  private handleOnline(): void {
    console.log('[NetworkStatus] Online event fired');
    this.browserOnlineHint = true;
    this.verifyInternetReachability('online_event');
  }

  private handleOffline(): void {
    this.browserOnlineHint = false;
    this.internetReachable = false;
    this.updateOnlineState('offline_event');
    // Toast notification is handled by app.component.ts to avoid duplicates
  }

  private handleConnectionChange(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const networkInfo = this.getNetworkInformation();
    const typeIndicatesOffline = networkInfo?.type === 'none';
    this.browserOnlineHint = navigator.onLine && !typeIndicatesOffline;

    if (!this.browserOnlineHint) {
      this.internetReachable = false;
      this.updateOnlineState('connection_change_offline');
      return;
    }

    this.verifyInternetReachability('connection_change');
  }

  private getNetworkInformation(): NetworkInformationLike | undefined {
    if (!isPlatformBrowser(this.platformId)) {
      return undefined;
    }

    const nav = navigator as NavigatorWithConnection;
    return nav.connection || nav.mozConnection || nav.webkitConnection;
  }

  /**
   * Report realtime transport state (Pusher) to improve internet detection.
   * This is a signal only - final online/offline state is confirmed by probe.
   */
  reportPusherState(state: string): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    if (state === 'connected') {
      this.browserOnlineHint = true;
      this.schedulePusherReachabilityProbe('pusher_connected');
      return;
    }

    // Ignore transitional states unless browser itself says offline.
    if (state === 'connecting') {
      return;
    }

    if (
      state === 'disconnected' ||
      state === 'unavailable' ||
      state === 'failed'
    ) {
      this.schedulePusherReachabilityProbe(`pusher_${state}`);
    }
  }

  private schedulePusherReachabilityProbe(reason: string): void {
    if (!this.browserOnlineHint) {
      return;
    }

    if (this.pusherProbeTimer) {
      clearTimeout(this.pusherProbeTimer);
    }

    this.pusherProbeTimer = setTimeout(() => {
      this.pusherProbeTimer = null;
      this.verifyInternetReachability(reason);
    }, this.pusherProbeDebounceMs);
  }

  private async verifyInternetReachability(reason: string): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    if (!this.browserOnlineHint) {
      this.internetReachable = false;
      this.updateOnlineState(`${reason}_hint_offline`);
      return;
    }

    if (this.reachabilityCheckInProgress) {
      return;
    }

    const now = Date.now();
    if (now - this.lastReachabilityCheckAt < this.reachabilityThrottleMs) {
      return;
    }

    this.reachabilityCheckInProgress = true;
    this.lastReachabilityCheckAt = now;

    try {
      const [apiReachable, pusherReachable] = await Promise.all([
        this.probeApiReachability(),
        this.probePusherReachability(),
      ]);

      this.internetReachable = apiReachable || pusherReachable;
      this.updateOnlineState(`reachability_${reason}`);
    } catch (error) {
      this.internetReachable = false;
      this.updateOnlineState(`reachability_${reason}_error`);
    } finally {
      this.reachabilityCheckInProgress = false;
    }
  }

  private async probeApiReachability(): Promise<boolean> {
    if (!isPlatformBrowser(this.platformId)) {
      return false;
    }

    let timeout: ReturnType<typeof setTimeout> | null = null;
    try {
      const controller = new AbortController();
      timeout = setTimeout(
        () => controller.abort(),
        this.reachabilityTimeoutMs
      );
      const probeUrl = `${environment.url}/network_probe?ts=${Date.now()}`;
      await fetch(probeUrl, {
        method: 'GET',
        mode: 'no-cors',
        cache: 'no-store',
        signal: controller.signal,
      });
      clearTimeout(timeout);
      return true;
    } catch (error) {
      return false;
    } finally {
      if (timeout) {
        clearTimeout(timeout);
      }
    }
  }

  private async probePusherReachability(): Promise<boolean> {
    let timeout: ReturnType<typeof setTimeout> | null = null;
    try {
      const controller = new AbortController();
      timeout = setTimeout(
        () => controller.abort(),
        this.reachabilityTimeoutMs
      );
      const probeUrl = `https://sockjs-${environment.PUSHER_APP_CLUSTER}.pusher.com/pusher/ping?ts=${Date.now()}`;
      await fetch(probeUrl, {
        method: 'GET',
        mode: 'no-cors',
        cache: 'no-store',
        signal: controller.signal,
      });
      clearTimeout(timeout);
      return true;
    } catch (error) {
      return false;
    } finally {
      if (timeout) {
        clearTimeout(timeout);
      }
    }
  }

  private updateOnlineState(reason: string): void {
    const nextOnline = this.browserOnlineHint && this.internetReachable;
    const previousOnline = this.isOnlineSubject.getValue();

    if (previousOnline === nextOnline) {
      return;
    }

    this.isOnlineSubject.next(nextOnline);
    console.log(
      `[NetworkStatus] online state changed (${reason}): ${previousOnline} -> ${nextOnline}`
    );

    if (nextOnline) {
      this.retryQueueWhenBackOnline();
    }
  }

  private retryQueueWhenBackOnline(): void {
    const queue = this.queuedRequestsSubject.getValue();
    console.log('[NetworkStatus] Queue length on online:', queue.length);
    if (queue.length === 0) {
      return;
    }

    setTimeout(() => {
      if (this.isOnlineSubject.getValue()) {
        console.log('[NetworkStatus] Starting retry after online');
        this.retryAllRequests();
      }
    }, 500);
  }

  /**
   * Check if a URL should be excluded from queueing
   */
  private shouldExcludeUrl(url: string): boolean {
    return this.excludedUrls.some((excluded) => url.includes(excluded));
  }

  /**
   * Queue a failed request for later retry
   * Note: Only POST/PUT/PATCH/DELETE are queued as they're state-changing.
   * GET requests are not queued - user should refresh to re-fetch data.
   */
  queueRequest(request: HttpRequest<any>): void {
    if (this.shouldExcludeUrl(request.url)) {
      return;
    }

    // Don't queue GET requests - they don't change state and the
    // component won't receive the retry result anyway
    if (request.method.toUpperCase() === 'GET') {
      console.log('[NetworkStatus] Skipping GET request (user should refresh):', request.url);
      return;
    }

    const queue = this.queuedRequestsSubject.getValue();

    // Check queue size limit
    if (queue.length >= this.MAX_QUEUE_SIZE) {
      // Remove oldest request
      queue.shift();
    }

    // Convert headers to plain object
    const headersObj: { [key: string]: string } = {};
    request.headers.keys().forEach((key) => {
      const value = request.headers.get(key);
      if (value) {
        headersObj[key] = value;
      }
    });

    const queuedRequest: QueuedRequest = {
      id: this.generateId(),
      method: request.method,
      url: request.url,
      body: request.body,
      headers: headersObj,
      timestamp: Date.now(),
      retryCount: 0,
    };

    queue.push(queuedRequest);
    this.queuedRequestsSubject.next([...queue]);
    this.saveQueueToStorage();

    // If we're online, schedule a retry after a short delay
    // This handles cases like 504 errors that happen while online
    if (this.isOnlineSubject.getValue() && !this.isRetryingSubject.getValue()) {
      setTimeout(() => {
        if (this.isOnlineSubject.getValue()) {
          this.retryAllRequests();
        }
      }, 2000); // Wait 2 seconds before retrying
    }
  }

  /**
   * Retry all queued requests
   */
  retryAllRequests(): void {
    const queue = [...this.queuedRequestsSubject.getValue()]; // Clone array
    console.log('[NetworkStatus] retryAllRequests called, queue:', queue.length, 'isRetrying:', this.isRetryingSubject.getValue());

    if (queue.length === 0) {
      console.log('[NetworkStatus] Queue is empty, nothing to retry');
      return;
    }

    if (this.isRetryingSubject.getValue()) {
      console.log('[NetworkStatus] Already retrying, skipping');
      return;
    }

    this.isRetryingSubject.next(true);
    console.log('[NetworkStatus] Starting to process', queue.length, 'requests');

    // Process requests sequentially with delay
    from(queue)
      .pipe(
        concatMap((request) => {
          console.log('[NetworkStatus] Processing request:', request.method, request.url);
          return this.retryRequest(request).pipe(delay(300));
        }),
        takeUntil(this.destroy$),
        finalize(() => {
          console.log('[NetworkStatus] Retry finished');
          this.isRetryingSubject.next(false);
        })
      )
      .subscribe({
        next: (result) => {
          console.log('[NetworkStatus] Request result:', result);
        },
        error: (err) => {
          console.error('[NetworkStatus] Retry queue error:', err);
          this.isRetryingSubject.next(false);
        },
        complete: () => {
          console.log('[NetworkStatus] All retries complete');
        }
      });
  }

  /**
   * Retry a single request
   */
  private retryRequest(queuedRequest: QueuedRequest): Observable<any> {
    console.log('[NetworkStatus] retryRequest called for:', queuedRequest.method, queuedRequest.url);

    // Add retry marker header so interceptor doesn't re-queue on failure
    const headers = new HttpHeaders(queuedRequest.headers)
      .set('X-Retry-Request', 'true');

    let request$: Observable<any>;

    switch (queuedRequest.method.toUpperCase()) {
      case 'GET':
        console.log('[NetworkStatus] Making GET request to:', queuedRequest.url);
        request$ = this.http.get(queuedRequest.url, { headers });
        break;
      case 'POST':
        console.log('[NetworkStatus] Making POST request to:', queuedRequest.url);
        request$ = this.http.post(queuedRequest.url, queuedRequest.body, { headers });
        break;
      case 'PUT':
        console.log('[NetworkStatus] Making PUT request to:', queuedRequest.url);
        request$ = this.http.put(queuedRequest.url, queuedRequest.body, { headers });
        break;
      case 'PATCH':
        console.log('[NetworkStatus] Making PATCH request to:', queuedRequest.url);
        request$ = this.http.patch(queuedRequest.url, queuedRequest.body, { headers });
        break;
      case 'DELETE':
        console.log('[NetworkStatus] Making DELETE request to:', queuedRequest.url);
        request$ = this.http.delete(queuedRequest.url, { headers });
        break;
      default:
        console.log('[NetworkStatus] Unknown method:', queuedRequest.method);
        request$ = of(null);
    }

    return request$.pipe(
      tap({
        next: (response) => {
          console.log('[NetworkStatus] Request SUCCESS:', queuedRequest.url);
          // Success - remove from queue
          this.removeFromQueue(queuedRequest.id);
        },
      }),
      catchError((error) => {
        console.warn('[NetworkStatus] Request FAILED:', queuedRequest.url, 'Status:', error?.status);
        // Failed - increment retry count or remove if max attempts reached
        queuedRequest.retryCount++;

        if (queuedRequest.retryCount >= this.MAX_RETRY_ATTEMPTS) {
          console.log('[NetworkStatus] Max retries reached, removing from queue');
          this.removeFromQueue(queuedRequest.id);
        } else {
          console.log('[NetworkStatus] Will retry later, attempt:', queuedRequest.retryCount);
          this.updateQueuedRequest(queuedRequest);
        }

        return of(null);
      })
    );
  }

  /**
   * Remove a request from the queue
   */
  removeFromQueue(id: string): void {
    const queue = this.queuedRequestsSubject.getValue();
    const updatedQueue = queue.filter((req) => req.id !== id);
    this.queuedRequestsSubject.next(updatedQueue);
    this.saveQueueToStorage();
  }

  /**
   * Update a queued request
   */
  private updateQueuedRequest(updatedRequest: QueuedRequest): void {
    const queue = this.queuedRequestsSubject.getValue();
    const index = queue.findIndex((req) => req.id === updatedRequest.id);
    if (index !== -1) {
      queue[index] = updatedRequest;
      this.queuedRequestsSubject.next([...queue]);
      this.saveQueueToStorage();
    }
  }

  /**
   * Clear the entire queue
   */
  clearQueue(): void {
    this.queuedRequestsSubject.next([]);
    this.saveQueueToStorage();
  }

  /**
   * Get queue count
   */
  getQueueCount(): number {
    return this.queuedRequestsSubject.getValue().length;
  }

  /**
   * Check if currently online
   */
  isOnline(): boolean {
    return this.isOnlineSubject.getValue();
  }

  /**
   * Save queue to localStorage
   */
  private saveQueueToStorage(): void {
    if (isPlatformBrowser(this.platformId)) {
      try {
        const queue = this.queuedRequestsSubject.getValue();
        localStorage.setItem(this.STORAGE_KEY, JSON.stringify(queue));
      } catch (e) {
        // Storage full or not available
      }
    }
  }

  /**
   * Load queue from localStorage
   */
  private loadQueueFromStorage(): void {
    if (isPlatformBrowser(this.platformId)) {
      try {
        const stored = localStorage.getItem(this.STORAGE_KEY);
        if (stored) {
          const queue = JSON.parse(stored) as QueuedRequest[];
          this.queuedRequestsSubject.next(queue);
        }
      } catch (e) {
        // Invalid data or not available
      }
    }
  }

  /**
   * Generate unique ID for request
   */
  private generateId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();

    if (isPlatformBrowser(this.platformId)) {
      window.removeEventListener('online', this.onlineEventHandler);
      window.removeEventListener('offline', this.offlineEventHandler);
      const networkInfo = this.getNetworkInformation();
      if (networkInfo?.removeEventListener) {
        networkInfo.removeEventListener('change', this.connectionChangeHandler);
      }
    }

    if (this.pusherProbeTimer) {
      clearTimeout(this.pusherProbeTimer);
      this.pusherProbeTimer = null;
    }
  }
}
